import { useQuery } from '@tanstack/react-query';
import {
  Site,
  sitesApi,
  SiteAssignment,
  OrganizationMember,
  organizationsApi,
  OrganizationInvitation,
  SiteStatsResponse,
  FindingsTrendResponse,
  TrendPeriodType,
} from '../lib/organizations';

/**
 * 현장 목록 조회 훅
 *
 * groupId는 그룹 필터가 걸린 메뉴(현장 관리 등)만 전달한다. 현장 할당 후보 목록처럼
 * 그룹 밖 현장까지 골라야 하는 호출부는 생략해 전체 범위를 유지한다.
 */
export function useSites(companyId: string | undefined, groupId?: string | null) {
  return useQuery({
    queryKey: ['sites', companyId, groupId ?? null],
    queryFn: () => sitesApi.findAll(companyId!, groupId),
    enabled: !!companyId,
  });
}

/**
 * 현장 상세 조회 훅
 */
export function useSite(companyId: string | undefined, siteId: string | undefined) {
  return useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesApi.findById(companyId!, siteId!),
    enabled: !!companyId && !!siteId,
  });
}

/**
 * 현장 담당자 목록 조회 훅
 */
export function useSiteAssignments(companyId: string | undefined, siteId: string | undefined) {
  return useQuery({
    queryKey: ['siteAssignments', siteId],
    queryFn: () => sitesApi.getAssignments(companyId!, siteId!),
    enabled: !!companyId && !!siteId,
  });
}

/**
 * 조직 멤버 목록 조회 훅
 */
export function useOrganizationMembers(companyId: string | undefined) {
  return useQuery({
    queryKey: ['organizationMembers', companyId],
    queryFn: () => organizationsApi.getMembers(companyId!),
    enabled: !!companyId,
  });
}

/**
 * 조직 초대 목록 조회 훅
 */
export function useOrganizationInvitations(companyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['organizationInvitations', companyId],
    queryFn: () => organizationsApi.getInvitations(companyId!),
    enabled: !!companyId && enabled,
  });
}

/**
 * 현장별 초대 목록 조회 훅 (조직 초대에서 필터링)
 */
export function useSiteInvitations(
  companyId: string | undefined,
  siteId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: ['siteInvitations', siteId],
    queryFn: async () => {
      const invitations = await organizationsApi.getInvitations(companyId!);
      return invitations.filter((inv) => inv.siteId === siteId);
    },
    enabled: !!companyId && !!siteId && enabled,
  });
}

/**
 * 현장 통계 조회 훅
 */
export function useSiteStats(companyId: string | undefined, siteId: string | undefined) {
  return useQuery({
    queryKey: ['siteStats', siteId],
    queryFn: () => sitesApi.getSiteStats(companyId!, siteId!),
    enabled: !!companyId && !!siteId,
  });
}

/**
 * 위험요인 추이 조회 훅
 */
export function useFindingsTrend(
  companyId: string | undefined,
  siteId: string | undefined,
  period: TrendPeriodType = 'daily'
) {
  return useQuery({
    queryKey: ['findingsTrend', siteId, period],
    queryFn: () => sitesApi.getFindingsTrend(companyId!, siteId!, period),
    enabled: !!companyId && !!siteId,
  });
}

// Re-export types for convenience
export type { Site, SiteAssignment, OrganizationMember, OrganizationInvitation, SiteStatsResponse, FindingsTrendResponse };
